import React from "react";
import Image from "next/image";
import { FundComparisonResult } from "@/type/schemeType";
import { ROOT_URL } from "@/utils/gloableVariables";

type Props = {
  fund: FundComparisonResult;
};

type returns = {
  three_year?: number | string | null;
  one_year?: number | string | null;
  one_month?: number | string | null;
};

export default function FundComparisonCard({ fund }: Props) {
  if (!fund) return null;

  const topReturns = {
    three_year: fund.scheme_first_three_year,
    one_year: fund.scheme_first_one_year,
    one_month: fund.scheme_first_one_month,
  };

  const bottomReturns = {
    three_year: fund.scheme_second_three_year,
    one_year: fund.scheme_second_one_year,
    one_month: fund.scheme_second_one_month,
  };

  const getReturnPeriod = () => {
    const isValid = (value: number | undefined) => value !== undefined && value !== null && value !== 0;

    const bothHave3Y = isValid(topReturns.three_year) && isValid(bottomReturns.three_year);
    const bothHave1Y = isValid(topReturns.one_year) && isValid(bottomReturns.one_year);
    const bothHave1M = isValid(topReturns.one_month) && isValid(bottomReturns.one_month);

    if (bothHave3Y) return { key: "three_year", label: "3Y Returns", unit: "p.a." };
    if (bothHave1Y) return { key: "one_year", label: "1Y Returns", unit: "p.a." };
    if (bothHave1M) return { key: "one_month", label: "1M Returns", unit: "p.m." };

    if (isValid(topReturns.three_year)) return { key: "three_year", label: "3Y Returns", unit: "p.a." };
    if (isValid(topReturns.one_year)) return { key: "one_year", label: "1Y Returns", unit: "p.a." };
    return { key: "one_month", label: "1M Returns", unit: "p.m." };
  };

  const { key: returnKey, label: returnLabel, unit } = getReturnPeriod();

  return (
    <a
      href={`${ROOT_URL}mutual-fund/compare/${fund.compare_url}`}
      className="w-full max-w-65 shrink-0 overflow-hidden rounded-2xl group transition duration-350 xs:hover:shadow-[0px_4px_8px_0px_#00000020] xs:hover:-translate-y-2"
    >
      <div className="border-2 border-[#1A51AA1A] rounded-2xl p-3 w-full bg-[#F5F9FF] space-y-3 transition duration-350 group-hover:border-[#1A51AA5C] ">
        {/* Top Fund */}
        <div className="flex gap-2 items-start h-14.5">
          <span className="rounded-full w-7 h-7 bg-[#F8F8F8] shrink-0 inline-block mt-1 overflow-hidden">
            {fund.first_amc_code && (
              <Image
                src={`${ROOT_URL}/img/AMCsLogo/${fund.first_amc_code}.png`}
                alt={fund.scheme_first_name}
                width={30}
                height={30}
                className="object-contain mb-1"
                unoptimized
              />
            )}
          </span>
          <div>
            <p className="text-sm leading-4 text-left text-primaryBlack font-medium line-clamp-2 transition duration-350 group-hover:text-primaryBlue">
              {fund.scheme_first_name}
            </p>

            <p className="text-sm leading-4 text-[#444444] mt-2">
              {returnLabel}:{" "}
              <span className="text-[#1F840D] font-bold">
                {(topReturns as returns)[returnKey as keyof returns] ?? 0} ({unit})
              </span>
            </p>
          </div>
        </div>

        {/* VS divider */}
        <div className="my-4 h-8 flex items-center justify-center">
          <div className="w-full border-t border-dashed border-primaryBlue relative">
            <span className="absolute w-8 h-8 inline-flex items-center justify-center top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-medium text-primaryBlue bg-white border border-primaryBlue rounded-full">
              VS
            </span>
          </div>
        </div>

        {/* Bottom Fund or Add Button */}

        <div className="flex gap-2 items-start h-14.5">
          <span className="rounded-full w-7 h-7 bg-[#F8F8F8] shrink-0 inline-block mt-1 overflow-hidden">
            <Image
              src={`${ROOT_URL}/img/AMCsLogo/${fund.amc_code}.png`}
              alt={fund.scheme_second_name}
              width={30}
              height={30}
              className="object-contain mb-1"
              unoptimized
            />
          </span>
          <div>
            <p className="text-sm leading-4 text-left text-primaryBlack font-medium line-clamp-2 transition duration-350 group-hover:text-primaryBlue">
              {fund.scheme_second_name}
            </p>

            <p className="text-sm leading-4 text-[#444444] mt-2">
              {returnLabel}:{" "}
              <span className="text-[#1F840D] font-bold">
                {(bottomReturns as returns)[returnKey as keyof returns] ?? 0} ({unit})
              </span>
            </p>
          </div>
        </div>
      </div>
    </a>
  );
}
